import React from 'react';
import { StatusPill } from './StatusPill';

interface SessionMonitorCardProps {
  session: any;
  assessmentTitle: string;
  getTimeSince: (timestamp: number) => string;
  getStatusColor: (lastHeartbeat: number) => string;
  getStatusLabel: (lastHeartbeat: number) => string;
}

export const SessionMonitorCard: React.FC<SessionMonitorCardProps> = ({
  session,
  assessmentTitle,
  getTimeSince,
  getStatusColor,
  getStatusLabel,
}) => {
  const status = getStatusColor(session.lastHeartbeat);
  const progressPercent = Math.round((session.progress.completedProblems / session.progress.totalProblems) * 100);

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all">
      <div className={`h-1 w-full bg-${status}`}></div>
      <div className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h4 className="text-lg font-bold">{session.userName || session.userEmail || 'Anonymous User'}</h4>
            <p className="text-sm text-muted-foreground truncate max-w-[200px]">
              {assessmentTitle}
            </p>
          </div>
          <StatusPill 
            status={getStatusLabel(session.lastHeartbeat).toLowerCase() as any} 
            label={getStatusLabel(session.lastHeartbeat)}
          />
        </div>

        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-xs mb-1.5">
              <span className="font-medium text-muted-foreground">Progress</span>
              <span className="font-bold">{session.progress.completedProblems} / {session.progress.totalProblems} Problems</span>
            </div>
            <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-500" 
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 py-3 border-y border-border/50">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-0.5">Started</p>
              <p className="text-sm font-medium">{getTimeSince(session.startTime)} ago</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-0.5">Focus Lost</p>
              <p className={`text-sm font-medium ${session.focusLostCount > 0 ? 'text-destructive' : ''}`}>
                {session.focusLostCount} times
              </p>
            </div>
          </div>

          <div className="flex justify-between items-center pt-1">
            <div className="flex flex-col">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Last Active</span>
              <span className="text-xs font-medium">{getTimeSince(session.lastHeartbeat)} ago</span>
            </div>
            {session.isPaused && (
              <span className="bg-warning/20 text-warning text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wide">
                Paused
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

